<template>
	<div
		ref="wrapper"
		class="player js-player"
		:class="wrapperClasses"
	>
		<div
			ref="mediaContainer"
			class="media-container"
			:class="{ 'has-takeover-ad': renderedTakeoverAdState }"
		>
			<div
				class="player__takeover-stage"
				:style="takeoverStageStyle"
			>
				<div
					v-if="renderedTakeoverAdState"
					class="player__takeover-background"
					:style="{ backgroundImage: `url(${renderedTakeoverAdState.imageUrl})` }"
				/>
				<video
					ref="video"
					class="js-video-el"
					:class="[`-${videoObjectFit}`]"
					:style="takeoverVideoStyle"
					playsinline
					:poster="autoplay ? null : poster"
					:preload="isMobileAppleDevice ? 'metadata' : 'auto'"
					:loop="loop ? '' : null"
				/>
				<div
					v-if="renderedTakeoverAdState"
					class="player__takeover-advertisement-label"
					:class="{ 'is-visible': takeoverAdState?.visible }"
					:style="takeoverAdvertisementLabelStyle"
				>
					{{ $I18n.trans('monetization.advertisement') }}
				</div>
			</div>
			<!-- loading spinner -->
			<div
				v-show="showLoadingSpinner"
				class="loading-spinner"
			>
				<LoadingSpinner
					:delay="0"
					center
				/>
			</div>
			<!-- over media slot -->
			<slot
				name="over"
				:player="this"
			/>
			<div
				class="player__ima-container js-ima-ad-container"
				:class="{ 'is-active': localGoogleImaIsActive }"
				:style="{ backgroundImage: `url(${poster})` }"
			/>
		</div>
		<!-- after media slot -->
		<slot
			name="after"
			:player="this"
		/>
	</div>
</template>

<script>
import Hls from 'hls.js';
import { VASTClient, VASTTracker } from '@dailymotion/vast-client';
import { throttle } from 'lodash-es';
import LoadingSpinner from '../loader/LoadingSpinner.vue';
import toggleFullscreen from '@videotools/js/util/toggleFullscreen';
import { isMobileAppleDevice, isSafari } from '@shared/utils/detectAppleDevice';
import { isInFullscreen } from '@shared/utils/detectFullscreenChange';
import { checkAutoplayWithSound } from '@videotools/js/util/media';
import VideoUtils from '@videotools/js/components/player/video-utils';
import { HLS_SUPPORT } from '@videotools/js/components/player/enums';
import { PLAYER_STATE } from '../player/enums';
import { bus, EVENT } from '@videotools/js/events';
import { mapGetters } from 'vuex';
import { getAudiotrackLabel } from '@shared/utils/audiotrack';
import { getCmcdAverages, recordCmcdFromUrl, startNativeBufferSampler } from './cmcd-metrics';

class CustomLoader extends Hls.DefaultConfig.loader {
	load(context, config, callbacks) {
		recordCmcdFromUrl(context.url);

		const onSuccess = callbacks.onSuccess;
		const onError = callbacks.onError;
		callbacks.onSuccess = (response, stats, context) => {
			if (response.code === 403) {
				bus.publish(EVENT.PLAYER.ON.ACCESS_DENIED);
			} else {
				onSuccess(response, stats, context);
			}
		};
		callbacks.onError = (response, context, networkDetails) => {
			console.error('HLS Playlist request failed: ' + JSON.stringify(response), networkDetails);
			bus.publish(EVENT.PLAYER.ON.REQUEST_FAILED);

			if (response.code === 403) {
				bus.publish(EVENT.PLAYER.ON.ACCESS_DENIED);
			} else {
				onError(response, context, networkDetails);
			}
		};

		super.load(context, config, callbacks);
	}
}

export const HLS_OPTIONS = {
	debug:        false,
	enableWorker: true,
	cmcd:         {
		useHeaders: false,
	},
	loader:               CustomLoader,
	// lowLatencyMode:       true,
	// maxBufferLength:      30,
	// maxBufferSize:        0,
	// backBufferLength:     120,
	// capLevelToPlayerSize: true,
	// maxBufferHole:        0,
	levelLoadingMaxRetry: 30,
};

const VIDEO_EVENT = {
	PLAY:            'play',
	PAUSE:           'pause',
	ENDED:           'ended',
	ERROR:           'error',
	LOADED_METADATA: 'loadedmetadata',
	TIME_UPDATE:     'timeupdate',
	DURATION_CHANGE: 'durationchange',
	VOLUME_CHANGE:   'volumechange',
	RATE_CHANGE:     'ratechange',
	PLAYING:         'playing',
	WAITING:         'waiting',
	PROGRESS:        'progress',
};
const HLS_EVENT = Hls.Events;

const CUSTOM_ERROR = {
	SOURCE_SIGNAL_NOT_ADVANCING:       'sourceSignalNotAdvancing',
	LEVEL_LOADING_MAX_RETRY_EXHAUSTED: 'levelLoadingMaxRetryExhausted',
};

const SEEK_FORWARD_BACKWARD_DELTA = 10;
const SEEK_DEBOUNCE_INTERVAL = isMobileAppleDevice() ? 100 : 10;
const NETWORK_ERROR_RETRY_DELAY = 5000;
const STREAM_NOT_ADVANCING_MAX_COUNT = 20;

const vastClient = new VASTClient();

const padLeft = (input, length, padChar = '0') => {
	return (Array.from({ length: length + 1 }).join(padChar) + input).slice(-1 * length);
};
const formatTime = (position) => {
	const minutes = Math.floor(position / 60);
	const seconds = Math.floor(position % 60);
	return `${padLeft(minutes, 2)}:${padLeft(seconds, 2)}`;
};

const getInitialErrorCount = () => {
	return {
		fatalMediaError:    0,
		bufferStalledError: 0,
		networkError:       0,
		requestFailedError: 0,
	};
};

export default {
	name:       'Player',
	components: { LoadingSpinner },
	props:      {
		url: {
			type:     String,
			required: true,
		},
		poster: {
			type:    String,
			default: '',
		},
		autoplay: {
			type:    Boolean,
			default: true,
		},
		vastUrl: {
			type:     String,
			required: false,
		},
		primaryPreRollAdUrl: {
			type:    String,
			default: null,
		},
		// capped duration of non vod events
		forcedDuration: {
			type:     Number,
			required: false,
			default:  undefined,
		},
		streamLeadTimeSeconds: {
			type:    Number,
			default: 0,
		},
		initialVolume: {
			type:    Number,
			default: 50,
		},
		adsDisabled: {
			type:    Boolean,
			default: false,
		},
		wasMutedManually: {
			type:    Boolean,
			default: false,
		},
		ignoreDuration: {
			type:    Boolean,
			default: false,
		},
		googleImaIsActive: {
			type:    Boolean,
			default: false,
		},
		forceMuted: {
			type:    Boolean,
			default: false,
		},
		loop: {
			type:    Boolean,
			default: false,
		},
		videoObjectFit: {
			type:    String,
			default: 'contain',
		},
	},
	data() {
		return {
			adDuration:                      0,
			adPosition:                      0,
			ads:                             [],
			preRollAds:                      [],
			currentAd:                       null,
			currentAdMedia:                  null,
			currentQualityLevelIndex:        -1,
			internalDuration:                0,
			internalLiveSyncPosition:        undefined,
			isMuted:                         this.forceMuted,
			internalVolume:                  this.initialVolume,
			position:                        0,
			qualityLevels:                   [],
			seekIntervalTimeout:             -1,
			speed:                           1,
			state:                           PLAYER_STATE.IDLE,
			hasLoadedFirstFrame:             false,
			enableAutoQuality:               true,
			isReady:                         false,
			audioTracks:                     [],
			currentAudioTrackId:             null,
			initialAudioTrackSet:            false,
			showLoadingSpinnerTimeout:       null,
			showLoadingSpinner:              false,
			lastUnclearedHLSError:           null,
			streamNotAdvancedCount:          0,
			hasEnsuredFirstAudioTrackIsUsed: false,
			timeUpdateCount:                 0,
			timeUpdateTimeout:               null,
			manifestReloadTimeout:           null,
			errorCount:                      getInitialErrorCount(),
			googleIma:                       null,
			canAutoplayWithSound:            false,
			localGoogleImaIsActive:          this.googleImaIsActive,
			videoEndPositionReached:         false,
			hlsPlaylistHasEnded:             false,
			isUsingNativeHlsPlayback:        false,
			videoIsWaiting:                  false,
			takeoverAdState:                 null,
			renderedTakeoverAdState:         null,
			takeoverAdStateClearTimeout:     null,
			takeoverAdStateRequestId:        0,
			takeoverStageRect:               null,
			takeoverResizeObserver:          null,
		};
	},
	computed: {
		...mapGetters({
			deviceOrientation:   'device/orientation',
			isMobile:            'device/isMobile',
			isFullscreen:        'device/fullscreen',
			isLandscape:         'device/isLandscape',
			isMobileLandscape:   'device/isMobileLandscape',
			isMobileAppleDevice: 'device/isMobileAppleDevice',
			isTabletAppleDevice: 'device/isTabletAppleDevice',
		}),
		videoEl() {
			return this.$refs.video;
		},
		wrapperClasses() {
			return {
				mobile:     this.isMobile,
				fullscreen: this.isFullscreen,
				landscape:  this.isMobileLandscape,
			};
		},
		hasForcedDuration() {
			return Number.isFinite(this.forcedDuration);
		},
		/**
		 * User-facing duration in user time (streamLeadTimeSeconds subtracted, forcedDuration applied if set).
		 * This is what should be displayed to users and used for position calculations.
		 * Emitted as 'duration' event to parent components.
		 */
		duration() {
			if (this.hasForcedDuration) return this.forcedDuration;
			return this.internalDuration - this.streamLeadTimeSeconds;
		},
		/**
		 * Raw media duration in video element time (includes streamLeadTimeSeconds, not adjusted for forcedDuration).
		 * Used internally for tracking actual stream duration and detecting when stream exceeds event duration.
		 * Emitted as 'media-duration' event to parent components.
		 */
		mediaDuration() {
			return this.internalDuration;
		},
		liveSyncPosition() {
			return this.internalLiveSyncPosition;
		},
		isIdle() {
			return [PLAYER_STATE.IDLE, PLAYER_STATE.AD_IDLE].includes(this.state);
		},
		isPlaying() {
			return [PLAYER_STATE.PLAYING, PLAYER_STATE.AD_PLAYING].includes(this.state);
		},
		isPaused() {
			return [PLAYER_STATE.PAUSED, PLAYER_STATE.AD_PAUSED].includes(this.state);
		},
		isAdMode() {
			return [PLAYER_STATE.AD_IDLE, PLAYER_STATE.AD_PLAYING, PLAYER_STATE.AD_PAUSED].includes(this.state);
		},
		hasPreRollAd() {
			return !!this.primaryPreRollAdUrl || !!this.vastUrl;
		},
		hlsSupport() {
			return VideoUtils.getHlsSupport();
		},
		audioTrackListSupport() {
			return VideoUtils.getAudioTrackListSupport();
		},
		currentQualityLevel() {
			if (this.currentQualityLevelIndex === -1) return null;
			if (this.currentQualityLevelIndex >= this.qualityLevels.length) return null;
			return this.qualityLevels[this.currentQualityLevelIndex];
		},
		formattedPosition() {
			return formatTime(this.position);
		},
		formattedDuration() {
			return formatTime(this.duration);
		},
		formattedAdPosition() {
			return formatTime(this.adPosition);
		},
		formattedAdDuration() {
			return formatTime(this.adDuration);
		},
		takeoverStageStyle() {
			if (!this.takeoverStageRect) {
				return {};
			}

			return {
				width:  `${this.takeoverStageRect.width}px`,
				height: `${this.takeoverStageRect.height}px`,
			};
		},
		currentAdIsSkippable() {
			return this.currentAdMedia?.isSkippable ?? false;
		},
		currentAdSkipTime() {
			return Math.max(0, (this.currentAdMedia?.skipDelay ?? 0) - this.adPosition);
		},
		currentAdIsSkippableAtCurrentPosition() {
			return this.currentAdIsSkippable && this.currentAdSkipTime === 0;
		},
		takeoverVideoStyle() {
			if (!this.renderedTakeoverAdState) {
				return {};
			}

			const scale = this.takeoverAdState?.visible
				? Math.max(60, Math.min(Number(this.takeoverAdState.playerScalePercent) || 80, 100))
				: 100;
			const position = this.renderedTakeoverAdState.takeoverPosition;
			const style = {
				position:   'absolute',
				width:      `${scale}%`,
				height:     `${scale}%`,
				zIndex:     1,
				transition: 'width 250ms ease, height 250ms ease',
			};

			if (position.includes('top')) {
				style.bottom = 0;
			} else {
				style.top = 0;
			}

			if (position.includes('left')) {
				style.right = 0;
			} else {
				style.left = 0;
			}

			return style;
		},
		takeoverAdvertisementLabelStyle() {
			if (!this.renderedTakeoverAdState) {
				return {};
			}

			const takeoverVideoStyle = { ...this.takeoverVideoStyle };
			const position = this.renderedTakeoverAdState.takeoverPosition;
			delete takeoverVideoStyle.transition;

			return {
				...takeoverVideoStyle,
				alignItems:     position.includes('top') ? 'flex-start' : 'flex-end',
				justifyContent: position.includes('left') ? 'flex-start' : 'flex-end',
				zIndex:         2,
			};
		},
	},
	watch: {
		url() {
			this.init();
		},
		internalVolume(value) {
			const currentValue = this.getVolume();
			if (currentValue === value) return;
			this.setVolume(value);
		},
		ads(items) {
			if (items.length === 0) {
				return;
			}
			const ad = items[0];
			if (ad.media?.[0]?.link) {
				this.$emit('ad-link-change', ad.media[0].link);
			}
			this.rollAd(ad);
		},
		position(value) {
			this.$emit('position', value);
		},
		duration(value) {
			this.$emit('duration', value);
		},
		mediaDuration(value) {
			this.$emit('media-duration', value);
		},
		state(value) {
			this.$emit('state', value);
			if (value === PLAYER_STATE.PLAYING) {
				bus.publish(EVENT.PLAYER.ON.PLAY);
			}
			if (value === PLAYER_STATE.PAUSED) {
				bus.publish(EVENT.PLAYER.ON.PAUSE);
			}
		},
		qualityLevels(value) {
			this.$emit('quality-levels', value);
		},
		speed(value) {
			this.$emit('speed', value);
		},
		audioTracks(value) {
			if (value.length > 0 && !this.hasEnsuredFirstAudioTrackIsUsed) {
				this.hasEnsuredFirstAudioTrackIsUsed = true;
				this.setAudioTrack(value[0].id);
			}
			this.$emit('audio-tracks-update', value);
		},
		currentAudioTrackId(value) {
			this.$emit('audio-track-change', value);
		},
		deviceOrientation(value) {
			// this is a hack to fix a bug in iOS Safari where the aspect ratio is no more respected when the orientation changes
			if (this.isMobileAppleDevice && value === 'portrait') {
				const mediaContainer = this.$refs.mediaContainer;
				if (!mediaContainer) return;
				mediaContainer.style.aspectRatio = 'auto';
				window.requestAnimationFrame(() => {
					// The player can be destroyed before this frame runs during an orientation change.
					if (!this.$refs.mediaContainer) return;
					this.$refs.mediaContainer.style.aspectRatio = '16/9';
				});
			}
		},
		googleImaIsActive(value) {
			this.localGoogleImaIsActive = value;
		},
		videoEndPositionReached(value) {
			this.$emit('video-end-position-reached', value);
		},
	},
	mounted() {
		if (!this.videoEl) {
			const details = {
				isAdMode:       this.isAdMode,
				hlsIsSupported: this.hlsSupport,
			};
			window.toast.error(this.$I18n.trans('errors.client.browser_error'));
			console.error('Video element not found', details);
			return;
		}

		this.initTakeoverStageMeasurement();
		this.init();
	},
	beforeDestroy() {
		this.destroy();
	},
	methods: {
		// base
		async init() {
			// reset
			this.currentQualityLevelIndex = -1;
			this.qualityLevels = [];
			this.hasLoadedFirstFrame = false;
			this.videoEndPositionReached = false;
			this.hlsPlaylistHasEnded = false;
			this.isUsingNativeHlsPlayback = false;
			this.adPosition = 0;
			this.adDuration = 0;
			this.videoEl.pause();
			this.videoEl.volume = this.initialVolume / 100;
			this.hasEnsuredFirstAudioTrackIsUsed = false;

			if (this.hasPreRollAd && !this.adsDisabled) {
				this.state = PLAYER_STATE.AD_IDLE;
			}

			// If autoplay is enabled, we need to mute both videos and unmute them if possible
			if (this.autoplay) {
				this.mute();
				await this.unmuteOnAutoplayIfPossible();
			}

			this.videoEl.crossOrigin = 'anonymous';

			if (!this.adsDisabled && this.hasPreRollAd) {
				await this.initAds();
			} else {
				this.initMainPlayer();
			}

			// Track failed requests caught by CustomLoader
			bus.subscribe(EVENT.PLAYER.ON.REQUEST_FAILED, () => {
				// handling of request failed errors for ad playback is handled by PLAYER.ON.REQUEST_FAILED
				if (!this.isAdMode) {
					this.errorCount.requestFailedError++;
				}
			});

			this.videoEl.autoplay = this.autoplay;

			if (this.videoEl.muted) {
				this.$emit('autoplayed-muted', true);
			}
			this.registerMediaEventListeners();
		},
		async initAds() {
			const isGoogleImaAd = (url) => url?.includes('g.doubleclick.net');

			if (this.primaryPreRollAdUrl) {
				this.preRollAds.push({
					url:           this.primaryPreRollAdUrl,
					isGoogleImaAd: isGoogleImaAd(this.primaryPreRollAdUrl),
					wasInitiated:  false,
				});
			}
			if (this.vastUrl) {
				this.preRollAds.push({
					url:           this.vastUrl,
					isGoogleImaAd: isGoogleImaAd(this.vastUrl),
					wasInitiated:  false,
				});
			}

			// Init first ad
			if (this.preRollAds[0].isGoogleImaAd) {
				await this.initGoogleImaAd(this.preRollAds[0]);
			} else {
				await this.initVastAd(this.preRollAds[0]);
			}
			this.preRollAds[0].wasInitiated = true;
		},
		async initVastAd(ad) {
			this.registerMediaEventListeners();
			const ads = await this.loadAds(ad.url);
			if (!ads || ads.length === 0) {
				this.skipAd();
			} else {
				this.$emit('preroll-ad-ready');
			}
		},
		async initGoogleImaAd(ad, autoplay = this.autoplay) {
			const GoogleImaHandler = (await import('./GoogleImaHandler.js')).default;
			this.googleIma = await GoogleImaHandler.init({
				callbacks: {
					skipAd: () => {
						this.skipAd({ isImaAd: true });
					},
					handleAdReady: () => {
						this.$emit('preroll-ad-ready');
					},
					handleAdError: (error) => {
						if (error?.type !== 'adError') {
							console.error('Google IMA Ad Error:', error);
						}
						this.skipAd({ isImaAd: true });
					},
					handleAdStarted: () => {
						this.state = PLAYER_STATE.AD_PLAYING;
					},
					setAdDuration: (duration) => {
						this.adDuration = duration;
					},
					setAdPosition: (position) => {
						this.adPosition = position;
					},
					setGoogleImaActive: (active) => {
						this.$emit('set-google-ima-active', active);
					},
					syncGoogleImaVolume: () => {
						this.internalVolume = this.getVolume();
					},
					handleAdPaused: () => {
						this.state = PLAYER_STATE.AD_PAUSED;
					},
					handleAdResumed: () => {
						this.state = PLAYER_STATE.AD_PLAYING;
					},
				},
				autoplay,
				canAutoplayWithSound: this.canAutoplayWithSound,
				vastUrl:              ad.url,
			});
		},
		load(url, adIsHLS = null) {
			if (this.isAdMode && (!adIsHLS || !Hls.isSupported())) {
				this.videoEl.addEventListener('play', () => {
					// Track vast impression
					// Currently only one media file per vast ad is supported (the first element)
					if (this.currentAd?.media?.length > 0 && this.currentAd?.media[0].tracker) {
						this.currentAd?.media[0].tracker.trackImpression();
					}
				});
				this.videoEl.src = url;

				return;
			}

			// HLS
			// We use HLS if MSE is supported or if the browser doesn't support audioTrackList
			if (this.hlsSupport === HLS_SUPPORT.SUPPORTED_MSE || !this.audioTrackListSupport) {
				this.isUsingNativeHlsPlayback = false;
				if (this.$options.hls) {
					clearTimeout(this.manifestReloadTimeout);
					this.manifestReloadTimeout = null;
					this.$options.hls.stopLoad();
					this.$options.hls.detachMedia();
					this.$options.hls.destroy();
					this.$options.hls = null;
				}

				const hls = new Hls(HLS_OPTIONS);
				hls.attachMedia(this.videoEl);
				hls.on(HLS_EVENT.MEDIA_ATTACHED, () => {
					hls.loadSource(url);
				});

				this.$options.hls = hls;
			} else if ([HLS_SUPPORT.SUPPORTED_NATIVE, HLS_SUPPORT.SUPPORTED_NATIVE_AND_MSE].includes(this.hlsSupport)) {
				this.isUsingNativeHlsPlayback = true;
				this.$nextTick(() => {
					this.videoEl.src = url;
					this.videoEl.load();

					// Sample buffer length for native HLS (no CMCD available)
					this.startNativeBufferSampler();

					this.updateAudioTrackInformation();

					// audio tracks are available on play
					this.videoEl.addEventListener('play', () => {
						// Only update if currentAudioTrackId is not set yet
						if (this.currentAudioTrackId === -1) {
							this.updateAudioTrackInformation();
						}
					});

					this.videoEl.addEventListener('loadedmetadata', () => {
						this.updateAudioTrackInformation();
					});
					this.videoEl.addEventListener('loadeddata', () => {
						this.updateAudioTrackInformation();
					});

					this.videoEl.audioTracks.addEventListener('change', () => {
						this.currentAudioTrackId = Array.from(this.videoEl.audioTracks).findIndex(track => track.enabled);
					});

					if (!this.isAdMode) {
						this.videoEl.addEventListener('progress', () => {
							this.hasLoadedFirstFrame = true;
							this.$emit('first-frame-loaded');
						}, { once: true });
					}
				});
			} else {
				throw new Error('HLS is not supported');
			}
		},
		async loadAds(url) {
			try {
				const vast = await vastClient.get(url);
				const ads = vast.ads.map((ad) => {
					return {
						id:    ad.id,
						media: ad.creatives.map((creative) => {
							return {
								id:          creative.id,
								files:       creative.mediaFiles,
								isSkippable: Number.isFinite(creative.skipDelay),
								skipDelay:   creative.skipDelay,
								tracker:     new VASTTracker(vastClient, ad, creative),
								link:        creative.videoClickThroughURLTemplate?.url,
							};
						}),
					};
				});
				this.ads = ads;
				return ads;
			} catch (error) {
				console.error('Error loading VAST because of AdBlocker:', error);
			}
		},
		registerMediaEventListeners() {
			if (this.localGoogleImaIsActive) {
				return;
			}

			// subscribe to native events
			if (!this.videoEventsRegistered) {
				this.videoEventsRegistered = true;
				for (const event of Object.values(VIDEO_EVENT)) {
					this.videoEl.addEventListener(event, (...args) => {
						this.handleNativeEvent(event, ...args);
					});
				}
			}
			// subscribe to HLS events
			if (this.$options.hls) {
				for (const event of Object.values(HLS_EVENT)) {
					this.$options.hls.on(event, (...args) => {
						this.handleHLSEvent(...args);
					});
				}
			}
		},
		// media handlers
		getPosition() {
			return Math.max(0, (this.videoEl?.currentTime ?? 0) - this.streamLeadTimeSeconds);
		},
		/**
		 * Gets the raw duration from the video element (videoEl.duration or seekable range).
		 * Returns duration in video element's native time (includes streamLeadTimeSeconds, not capped).
		 * Used internally to get the actual video duration before applying any capping.
		 *
		 * Note: In Safari the duration is not accurate. It can be too low (even lower than playback position).
		 * It's correct only once the video reaches the end.
		 */
		getRawVideoDuration() {
			let duration = 0;
			if (Number.isFinite(this.videoEl.duration)) {
				duration = this.videoEl.duration;
			} else if (this.videoEl.seekable.length > 0) {
				duration = this.videoEl.seekable.end(this.videoEl.seekable.length - 1);
			}

			if (!Number.isFinite(duration)) {
				return 0;
			}

			return Math.max(0, duration);
		},
		/**
		 * Gets the video duration capped at forcedDuration + streamLeadTimeSeconds if forcedDuration is set.
		 * Returns duration in video element's native time (includes streamLeadTimeSeconds, capped if needed).
		 * Used to update internalDuration property (which feeds into mediaDuration computed property)
		 * and for seek bounds checking.
		 */
		getInternalDuration() {
			const rawDuration = this.getRawVideoDuration();

			// Cap duration at forcedDuration + streamLeadTimeSeconds if forcedDuration is set
			// This prevents mediaDuration from growing beyond event end when stream continues
			if (this.hasForcedDuration) {
				const maxDuration = this.forcedDuration + this.streamLeadTimeSeconds;
				return Math.min(rawDuration, maxDuration);
			}

			return rawDuration;
		},
		/**
		 * Gets the user-facing duration (same as duration computed property).
		 * Exposed as a method for parent components that access player via refs or playerBus.
		 * Returns duration in user time (streamLeadTimeSeconds subtracted, forcedDuration applied if set).
		 */
		getDuration() {
			return this.duration;
		},
		getAdPosition() {
			return Math.floor(this.videoEl?.currentTime ?? 0);
		},
		getAdDuration() {
			return Math.floor(this.videoEl?.duration ?? 0);
		},
		getVolume() {
			return (this.videoEl?.volume ?? 0) * 100;
		},
		getIsMuted() {
			return Boolean(this.videoEl?.muted);
		},
		getSpeed() {
			return this.videoEl?.playbackRate ?? 1;
		},
		getQualityLevel() {
			return this.currentQualityLevel;
		},
		async unmuteOnAutoplayIfPossible() {
			if (this.wasMutedManually || this.forceMuted) {
				return;
			}

			this.canAutoplayWithSound = await checkAutoplayWithSound();

			if (this.canAutoplayWithSound) {
				this.unmute();
			} else {
				// on iOS the video is played manually and therefore isn't muted
				if (this.getIsMuted()) {
					this.$emit('autoplayed-muted', true);
				}
			}
		},
		async play() {
			if (this.isAdMode) {
				if (this.localGoogleImaIsActive) {
					if (!this.googleIma) {
						return;
					}

					if (this.state === PLAYER_STATE.AD_PAUSED) {
						this.googleIma.resume();
					} else {
						this.googleIma.play();
					}
				} else {
					this.videoEl?.play().catch(() => {});
				}
			} else {
				if (this.getPosition() >= this.duration) {
					this.seek(0);
				}

				this.videoEl?.play().catch(() => {});
			}
		},
		pause() {
			this.videoEl?.pause();
		},
		stop(resetPosition = true) {
			this.pause();

			if (resetPosition) {
				this.seek(0);
			}
		},
		togglePlay() {
			if (this.isPlaying) {
				this.pause();
			} else {
				this.play();
			}
		},
		seek: throttle(function(position) {
			if (!this.videoEl) return;
			let value = 0;

			if (position < 0) {
				value = this.getInternalDuration() + position + this.streamLeadTimeSeconds;
			} else {
				value = position + this.streamLeadTimeSeconds;
			}

			const internalDuration = this.getInternalDuration();
			if (value > internalDuration) {
				value = internalDuration;
			}

			this.position = value - this.streamLeadTimeSeconds;
			this.videoEl.currentTime = value;
		}, SEEK_DEBOUNCE_INTERVAL),
		seekRelative(delta) {
			this.seek(this.getPosition() + delta);
		},
		seekForward(seconds = SEEK_FORWARD_BACKWARD_DELTA) {
			this.seekRelative(seconds);
		},
		seekBackward(seconds = SEEK_FORWARD_BACKWARD_DELTA) {
			this.seekRelative(-1 * seconds);
		},
		seekInterval(start, duration) {
			this.seek(Math.max(start, 0));
			this.play();
			clearTimeout(this.seekIntervalTimeout);
			this.seekIntervalTimeout = setTimeout(() => {
				this.pause();
			}, duration * 1000);
		},
		mute() {
			if (this.getIsMuted()) {
				return;
			}
			this.videoEl.muted = true;
			this.$emit('mute');
		},
		unmute() {
			if (this.localGoogleImaIsActive && this.googleIma) {
				this.googleIma.unmute(this.initialVolume);
			}

			if (!this.getIsMuted() || this.forceMuted) {
				return;
			}

			this.videoEl.muted = false;
			this.$emit('unmute');
			this.$emit('autoplayed-muted', false);
		},
		setVolume(value = 100) {
			const clampedValue = Math.max(0, Math.min(value, 100));
			if (clampedValue === this.getVolume()) {
				return;
			}

			// Update both player volumes
			this.videoEl.volume = clampedValue / 100;

			this.$emit('volume', clampedValue);
		},
		handleNativeVolumeChange() {
			const newVolume = this.getVolume();
			if (this.internalVolume !== newVolume) {
				this.internalVolume = newVolume;
				this.$emit('volume', this.internalVolume);
				this.unmute();
			}

			// mute / unmute
			const newIsMuted = this.getIsMuted();
			if (this.isMuted !== newIsMuted) {
				this.isMuted = newIsMuted;
				if (this.isMuted) {
					this.mute();
				} else {
					this.unmute();
				}
			}
		},
		setSpeed(value = 1) {
			this.videoEl.playbackRate = value;
		},
		setTakeoverAdState(state) {
			if (state?.visible) {
				this.updateTakeoverStageRect();
				clearTimeout(this.takeoverAdStateClearTimeout);
				this.takeoverAdStateClearTimeout = null;
				this.renderedTakeoverAdState = state;
				this.takeoverAdState = null;

				const requestId = ++this.takeoverAdStateRequestId;
				const image = new Image();
				const activateTakeover = () => {
					if (requestId !== this.takeoverAdStateRequestId) {
						return;
					}
					requestAnimationFrame(() => {
						if (requestId === this.takeoverAdStateRequestId) {
							this.takeoverAdState = state;
						}
					});
				};
				image.onload = activateTakeover;
				image.onerror = activateTakeover;
				image.src = state.imageUrl;

				if (image.complete) {
					activateTakeover();
				}
				return;
			}

			this.takeoverAdStateRequestId += 1;
			this.takeoverAdState = null;

			if (this.renderedTakeoverAdState) {
				clearTimeout(this.takeoverAdStateClearTimeout);
				this.takeoverAdStateClearTimeout = setTimeout(() => {
					this.renderedTakeoverAdState = null;
					this.takeoverAdStateClearTimeout = null;
				}, 250);
			}
		},
		initTakeoverStageMeasurement() {
			this.updateTakeoverStageRect();

			if (window.ResizeObserver && this.$refs.mediaContainer) {
				this.takeoverResizeObserver = new ResizeObserver(this.updateTakeoverStageRect);
				this.takeoverResizeObserver.observe(this.$refs.mediaContainer);
				return;
			}

			window.addEventListener('resize', this.updateTakeoverStageRect);
		},
		destroyTakeoverStageMeasurement() {
			if (this.takeoverResizeObserver) {
				this.takeoverResizeObserver.disconnect();
				this.takeoverResizeObserver = null;
			}

			window.removeEventListener('resize', this.updateTakeoverStageRect);
			this.takeoverStageRect = null;
		},
		destroyTakeoverAdState() {
			this.takeoverAdStateRequestId += 1;
			clearTimeout(this.takeoverAdStateClearTimeout);
			this.takeoverAdStateClearTimeout = null;
			this.takeoverAdState = null;
			this.renderedTakeoverAdState = null;
		},
		updateTakeoverStageRect() {
			const mediaContainer = this.$refs.mediaContainer;
			if (!mediaContainer) {
				return;
			}

			const { width, height } = mediaContainer.getBoundingClientRect();
			if (!width || !height) {
				return;
			}

			const stageWidth = Math.min(width, height * (16 / 9));
			const stageHeight = stageWidth * (9 / 16);
			const rect = {
				width:  stageWidth,
				height: stageHeight,
				left:   (width - stageWidth) / 2,
				top:    (height - stageHeight) / 2,
			};

			this.takeoverStageRect = rect;
			this.$emit('takeover-stage-rect-change', rect);
		},
		setQuality(value = 0) {
			if (value === 0) this.enableAutoQuality = true;
			else this.enableAutoQuality = false;
			if (value >= this.qualityLevels.length) return;
			this.$options.hls.loadLevel = value - 1;
			this.$options.hls.nextLevel = value - 1;
		},
		toggleFullscreen() {
			if (this.isFullscreen && !isSafari()) {
				if (isInFullscreen()) {
					toggleFullscreen(this.videoEl);
				}
			} else if (this.isMobileAppleDevice) {
				toggleFullscreen(this.videoEl);
			} else {
				bus.publish(EVENT.FULLSCREEN_TOGGLE);
			}
		},
		async togglePictureInPicture() {
			await VideoUtils.togglePictureInPicture(this.videoEl);
		},
		// event handling
		handleNativeEvent(event, ...args) {
			switch (event) {
			// TIME_UPDATE needs to be before other events in order to update the adPosition upfront
			case VIDEO_EVENT.TIME_UPDATE:
				this.checkForVideoEndPositionReached();
				if (this.isAdMode) {
					this.handleAdTimeUpdate();
				} else {
					this.handlePlayerTimeUpdate();
				}
				break;
			case VIDEO_EVENT.ERROR:
				if (this.isAdMode) {
					this.skipAd();
				} else {
					this.$emit('error', ...args);
				}
				break;
			case VIDEO_EVENT.PLAY:
				this.state = this.isAdMode ? PLAYER_STATE.AD_PLAYING : PLAYER_STATE.PLAYING;
				this.internalVolume = this.getVolume();
				this.isMuted = this.getIsMuted();
				this.speed = this.getSpeed();

				if (this.isAdMode) {
					this.$emit('ad-play');
				} else {
					this.internalDuration = this.getInternalDuration();
				}
				break;
			case VIDEO_EVENT.VOLUME_CHANGE:
				this.handleNativeVolumeChange();
				break;
			case VIDEO_EVENT.RATE_CHANGE:
				this.speed = this.getSpeed();
				this.$emit('speed', this.speed);
				break;
			case VIDEO_EVENT.WAITING:
				this.videoIsWaiting = true;
				this.enableLoadingSpinner();
				break;
			case VIDEO_EVENT.PLAYING:
				this.videoIsWaiting = false;
				this.disableLoadingSpinner();
				break;
			case VIDEO_EVENT.DURATION_CHANGE:
			case VIDEO_EVENT.LOADED_METADATA:
				if (this.isAdMode) {
					this.adDuration = this.getAdDuration();
					this.$emit('ad-duration', this.adDuration);
					this.broadcastAdTime();
				} else {
					this.internalDuration = this.getInternalDuration();

					if (!this.isReady) {
						this.isReady = true;
						this.$emit('ready');
					}
				}
				break;
			case VIDEO_EVENT.PAUSE:
				if (this.isAdMode) {
					this.state = PLAYER_STATE.AD_PAUSED;
				} else {
					this.state = PLAYER_STATE.PAUSED;
				}
				break;
			case VIDEO_EVENT.PROGRESS:
				// In Safari during live, we get the duration from the seekable property,
				// which is not available yet on durationchange or loadedmetadata events.
				// So we need to update the duration here for Safari during live.
				if (this.isAdMode) {
					this.adDuration = this.getAdDuration();
				} else {
					this.internalDuration = this.getInternalDuration();
				}
				break;
			case VIDEO_EVENT.ENDED:
				if (this.isAdMode) {
					this.skipAd({ watchedTillEnd: true });
				} else {
					this.state = PLAYER_STATE.PAUSED;
					// Native HLS does not expose playlist tags. Per HLS semantics, `ended`
					// confirms a bounded playlist while live-edge stalls use waiting/error events.
					if (this.isUsingNativeHlsPlayback) {
						this.videoEndPositionReached = (this.videoEl?.currentTime ?? 0) > 0;
					}
				}
				break;
			}
		},
		handleHLSEvent(event, data) {
			// Todo: We need to handle the following two erros:
			// 1: When Source Stabilizing is not active (anymore), 3Q creates a new playlist
			// we need refetch that new playlist from the playout endpoint
			// 2: When Source Stabilizing is active because no Signal arrives at 3Q, the video
			// progresses normal but the fragments are just black screens. To the player
			// everything seems normal.

			// update quality levels and current quality
			if (event === HLS_EVENT.LEVEL_LOADED) {
				const levelDetails = data?.details ?? {};
				// hls.js sets `live` to false when EXT-X-ENDLIST is present in the media playlist.
				if (!this.isAdMode) {
					this.hlsPlaylistHasEnded = levelDetails.live === false;
				}
				this.$emit('hls-playlist-type', {
					isLive:  levelDetails.live === true,
					isDvr:   levelDetails.live === false,
					details: {
						live:           levelDetails.live,
						type:           levelDetails.type,
						targetduration: levelDetails.targetduration,
						totalduration:  levelDetails.totalduration,
						startSN:        levelDetails.startSN,
						endSN:          levelDetails.endSN,
					},
				});

				const levels = this.$options.hls.levels.map((level) => {
					let label = level.bitrate;

					if (level.name) {
						label = level.name;
					} else if (level.height) {
						label = `${level.height}p`;
					}

					return {
						id:      level.id,
						label,
						bitrate: level.bitrate,
						width:   level.width,
						height:  level.height,
						url:     level.uri,
					};
				});
				this.qualityLevels = [{ id: -1, label: 'Auto' }, ...levels];

				if (!data.details?.advanced) {
					this.lastUnclearedHLSError = CUSTOM_ERROR.SOURCE_SIGNAL_NOT_ADVANCING;
					this.streamNotAdvancedCount++;
					this.$emit('error', {
						type:    'customError',
						details: CUSTOM_ERROR.SOURCE_SIGNAL_NOT_ADVANCING,
						isFatal: this.streamNotAdvancedCount >= STREAM_NOT_ADVANCING_MAX_COUNT,
						fatal:   this.streamNotAdvancedCount >= STREAM_NOT_ADVANCING_MAX_COUNT,
					});
				} else if (this.lastUnclearedHLSError === CUSTOM_ERROR.SOURCE_SIGNAL_NOT_ADVANCING) {
					this.lastUnclearedHLSError = null;
					this.$emit('error-clear');
				}

				if (data.details?.advanced) {
					this.streamNotAdvancedCount = 0;
				}
			}
			if (event === HLS_EVENT.LEVEL_SWITCHED) {
				if (this.enableAutoQuality) {
					this.currentQualityLevelIndex = 0;
				} else {
					this.currentQualityLevelIndex = this.$options.hls.currentLevel + 1;
				}
			}
			// handle hls errors
			if (event === HLS_EVENT.ERROR) {
				const { fatal: isFatal, type: errorType } = data;

				this.lastUnclearedHLSError = data;
				this.$emit('error', data);

				// https://hls-js.netlify.app/dist/hls-demo.js handleMediaError()
				if (isFatal && errorType === Hls.ErrorTypes.MEDIA_ERROR && data.details !== Hls.ErrorDetails.AUDIO_TRACK_LOAD_ERROR) {
					this.$options.hls.recoverMediaError();
					// Track fatal media errors
					this.errorCount.fatalMediaError++;
				}

				// Handle audio track load error
				if (isFatal && errorType === Hls.ErrorTypes.MEDIA_ERROR && data.details === Hls.ErrorDetails.AUDIO_TRACK_LOAD_ERROR) {
					if (this.audioTracks.length > 0) {
						this.setAudioTrack(this.audioTracks[0].id);
						this.$options.hls.startLoad();
					}
				}

				if (errorType === Hls.ErrorTypes.MEDIA_ERROR && data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
					this.enableLoadingSpinner();
					// Track buffer stalled error separately
					this.errorCount.bufferStalledError++;
				}

				if (errorType === Hls.ErrorTypes.NETWORK_ERROR) {
					// Track network errors
					this.errorCount.networkError++;

					// Check if levelLoadingMaxRetry was exhausted
					if (isFatal && data.details === Hls.ErrorDetails.LEVEL_LOAD_ERROR) {
						this.$emit('error', {
							type:          'customError',
							details:       CUSTOM_ERROR.LEVEL_LOADING_MAX_RETRY_EXHAUSTED,
							fatal:         true,
							originalError: data,
						});
					}
				}

				// If no Signal arrives at 3Q we get back this error. The player should try reload
				// the manifest based on the NETWORK_ERROR_RETRY_DELAY
				if (data.type === Hls.ErrorTypes.NETWORK_ERROR && data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR && data.response?.code === 404) {
					const hls = this.$options.hls;
					clearTimeout(this.manifestReloadTimeout);
					this.manifestReloadTimeout = setTimeout(() => {
						if (!hls || this.$options.hls !== hls) {
							return;
						}
						hls.loadSource(this.url);
						hls.startLoad();
					}, NETWORK_ERROR_RETRY_DELAY);
				}
			}

			// handle first frame load
			if (event === HLS_EVENT.FRAG_PARSED && !this.hasLoadedFirstFrame && !this.isAdMode) {
				this.hasLoadedFirstFrame = true;
				this.$emit('first-frame-loaded');

				if (this.isReady) {
					bus.publish(EVENT.PLAYER.ON.MEDIA_CHANGE);
				} else {
					this.isReady = true;
					this.$emit('ready');
				}
			}

			// handle successful fragment load
			if (this.lastUnclearedHLSError && event === HLS_EVENT.FRAG_BUFFERED && !data?.stats?.aborted) {
				this.lastUnclearedHLSError = null;
				this.$emit('error-clear');
			}

			// handle audio tracks update
			if (event === HLS_EVENT.AUDIO_TRACKS_UPDATED) {
				this.audioTracks = data.audioTracks.map((audioTrack, index) => ({
					id:            audioTrack.id,
					label:         getAudiotrackLabel(audioTrack, index),
					rawAudioTrack: audioTrack,
				}));
			}
			if (event === HLS_EVENT.AUDIO_TRACK_SWITCHED) {
				this.currentAudioTrackId = data.id;
			}

			// Update liveSyncPosition when fragments are loaded/parsed
			if ([HLS_EVENT.FRAG_LOADED, HLS_EVENT.FRAG_PARSED, HLS_EVENT.LEVEL_LOADED].includes(event)) {
				this.internalLiveSyncPosition = this.$options.hls?.liveSyncPosition ?? undefined;
			}
		},
		handleAdTimeUpdate() {
			this.adPosition = this.getAdPosition();
			this.$emit('ad-position', this.adPosition);
			this.broadcastAdTime();
		},
		checkForVideoEndPositionReached: throttle(function() {
			if (this.isAdMode) {
				return;
			}
			const playbackPosition = this.videoEl?.currentTime ?? 0;
			const isAtEnd = playbackPosition === this.getRawVideoDuration();
			const hasConfirmedStreamEnd = this.isUsingNativeHlsPlayback
				? this.videoEl?.ended === true
				: this.hlsPlaylistHasEnded && isAtEnd;
			if (this.videoEndPositionReached !== hasConfirmedStreamEnd) {
				// check for videoIsWaiting to avoid showing the snackbar while player is waiting for new chunks
				this.videoEndPositionReached = hasConfirmedStreamEnd && !this.videoIsWaiting && playbackPosition > 0;
			}
		}, 250),
		handlePlayerTimeUpdate() {
			if (this.state === PLAYER_STATE.PLAYING) {
				this.position = this.getPosition();
				if (!this.ignoreDuration && (this.position > this.duration)) {
					this.pause();
					this.seek(this.duration);
				}
			}
			// Timeupdate is fired between 4 and 66 times a second.
			// If fired less, the video isn't running smoothly and we should keep showing
			// the loading spinner
			// https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/timeupdate_event
			if (this.showLoadingSpinner || this.showLoadingSpinnerTimeout) {
				this.timeUpdateCount += 1;

				if (this.timeUpdateTimeout) {
					if (this.timeUpdateCount > 2) {
						this.disableLoadingSpinner();
						clearTimeout(this.timeUpdateTimeout);
						this.timeUpdateTimeout = null;
					}
					return;
				}
				this.timeUpdateTimeout = setTimeout(() => {
					this.timeUpdateCount = 0;
				}, 1000);
			}
		},
		broadcastAdTime() {
			const position = this.getAdPosition();
			const duration = this.getAdDuration();
			this.$emit('ad-time', { position, duration });
		},
		async rollAd(ad) {
			try {
				const url = new URL(ad.media[0].files[0].fileURL);

				this.pause();

				this.currentAd = ad;
				this.currentAdMedia = ad.media[0];

				const adIsHLS = url.pathname.endsWith('.m3u8');

				this.load(url.toString(), adIsHLS);
			} catch (error) {
				console.error('Error rolling ad:', error);
				this.skipAd();
			}
		},
		async skipAd(options = { isImaAd: false, watchedTillEnd: false }) {
			if (!options.isImaAd) {
				this.videoEl.pause();
			}

			if (options.isImaAd) {
				this.localGoogleImaIsActive = false;
			}

			const nextAd = this.preRollAds.find((ad) => !ad.wasInitiated);
			if (nextAd) {
				this.adPosition = 0;
				this.adDuration = 0;
				this.state = PLAYER_STATE.AD_IDLE;
				if (nextAd.isGoogleImaAd) {
					await this.initGoogleImaAd(nextAd, true);
				} else {
					await this.initVastAd(nextAd);
					this.play();
				}
				nextAd.wasInitiated = true;
			} else {
				this.state = PLAYER_STATE.IDLE;
				this.$emit('ad-skipped', { watchedTillEnd: options.watchedTillEnd });
				this.localGoogleImaIsActive = false;
				this.$emit('set-google-ima-active', false);
				this.$nextTick(async() => {
					this.initMainPlayer();

					if (options.isImaAd) {
						// IMA uses a separate player, so prepare the content element for autoplay again.
						this.mute();
						await this.unmuteOnAutoplayIfPossible();
						this.play();
					}
				});
			}
		},
		initMainPlayer() {
			// load media
			this.load(this.url);

			// init AirPlay
			this.initAirPlay();
			// init picture in picture
			this.initPictureInPicture();

			this.registerMediaEventListeners();

			// Emit ready event so we can load the extra data in parent components
			this.$emit('core-player-ready');

			// emit initial duration
			this.$emit('duration', this.duration);
		},
		snap() {
			const canvas = document.createElement('canvas');
			canvas.width = 256;
			canvas.height = 144;
			const context = canvas.getContext('2d');
			if (!context) return null;
			context.drawImage(this.videoEl, 0, 0, canvas.width, canvas.height);
			return canvas.toDataURL('image/png');
		},
		initAirPlay() {
			if (!window.WebKitPlaybackTargetAvailabilityEvent) {
				return;
			}

			this.videoEl.addEventListener('webkitplaybacktargetavailabilitychanged', (event) => {
				this.$emit('airplay-availability-change', {
					available: event.availability === 'available',
					videoEl:   this.videoEl,
				});
			});
		},
		initPictureInPicture() {
			if (this.videoEl) {
				const pipAvailable = VideoUtils.getPictureInPictureSupport(this.videoEl);
				this.$emit('picture-in-picture-availability-change', pipAvailable);
			}
		},
		setAudioTrack(id) {
			if (this.hlsSupport === HLS_SUPPORT.SUPPORTED_MSE || !this.audioTrackListSupport) {
				this.$options.hls.audioTrack = id;
			} else {
				const audioTracks = this.videoEl.audioTracks;
				if (!audioTracks || audioTracks.length === 0) return;
				for (let i = 0; i < audioTracks.length; i++) {
					audioTracks[i].enabled = (i === id);
				}
			}
		},
		enableLoadingSpinner(timeout = 300) {
			if (this.showLoadingSpinnerTimeout || this.showLoadingSpinner) {
				return;
			}
			clearTimeout(this.showLoadingSpinnerTimeout);
			this.showLoadingSpinnerTimeout = setTimeout(() => {
				this.showLoadingSpinner = true;
				this.showLoadingSpinnerTimeout = null;
			}, timeout);
		},
		disableLoadingSpinner() {
			clearTimeout(this.showLoadingSpinnerTimeout);
			this.showLoadingSpinnerTimeout = null;
			this.showLoadingSpinner = false;
		},
		destroy() {
			this.stop();
			this.stopNativeBufferSampler();
			this.destroyTakeoverAdState();
			this.destroyTakeoverStageMeasurement();
			clearTimeout(this.manifestReloadTimeout);
			this.manifestReloadTimeout = null;
			if (this.$options.hls) {
				this.$options.hls.destroy();
				this.$options.hls = null;
			}
		},
		startNativeBufferSampler() {
			this.stopNativeBufferSampler();
			this._nativeBufferSampler = startNativeBufferSampler(this.videoEl);
		},
		stopNativeBufferSampler() {
			if (this._nativeBufferSampler) {
				clearInterval(this._nativeBufferSampler);
				this._nativeBufferSampler = null;
			}
		},
		// iOS only audio handling
		updateAudioTrackInformation() {
			const list = this.videoEl?.audioTracks;
			const audioTracks = [];
			for (let i = 0; i < list.length; i++) {
				audioTracks.push({
					id:            i,
					label:         getAudiotrackLabel(list[i], i),
					enabled:       list[i].enabled,
					rawAudioTrack: list[i],
				});
			}
			this.audioTracks = audioTracks;
			this.currentAudioTrackId = audioTracks.findIndex((track) => track.enabled);
		},
		resetErrorCount() {
			this.errorCount = getInitialErrorCount();
		},
		getCmcdAverages() {
			return getCmcdAverages();
		},
		getCurrentQualityLevel() {
			try {
				if ([HLS_SUPPORT.SUPPORTED_NATIVE, HLS_SUPPORT.SUPPORTED_NATIVE_AND_MSE].includes(this.hlsSupport)) {
					return this.videoEl?.videoHeight || 0; // Return 0 or a default value if videoHeight is undefined or null
				} else {
					const levels = this.$options.hls?.levels;
					const currentLevel = this.$options.hls?.currentLevel;

					if (levels && typeof currentLevel === 'number' && levels[currentLevel]) {
						return levels[currentLevel].height || 0;
					}
				}
			} catch (error) {
				console.log('Error getting current video height: ', error);
			}

			return 0;
		},
		getCurrentAudioTrack() {
			const currentAudioTrackLabel = this.audioTracks?.find((option) => option.id == this.currentAudioTrackId)?.label;
			return currentAudioTrackLabel ?? null;
		},
	},
};
</script>

<style lang="scss" scoped>
@import "@shared/sass/shared-variables";

.player {
	position: relative;
	display: flex;
	flex-direction: column;
	line-height: 0;
	width: 100%;
	height: 100%;

	> div {
		line-height: normal;
	}

	.media-container {
		position: relative;
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		background: $color-black;

		&.has-takeover-ad {
			overflow: hidden;
		}
	}

	&:not(.mobile) .media-container {
		overflow: hidden;
	}
}

video {
	width: 100%;
	height: 100%;
	object-fit: contain;
	position: relative;
	z-index: 0;
	max-width: 100vw;
	max-height: 100vh;
	align-self: flex-start;

	&.-cover {
		object-fit: cover;
	}
}

.player__takeover-stage {
	position: relative;
	width: 100%;
	height: 100%;
	overflow: hidden;
	background-color: $color-black;
}

.player__takeover-background {
	position: absolute;
	inset: 0;
	background-position: center;
	background-size: cover;
	background-repeat: no-repeat;
	z-index: 0;
}

.player__takeover-advertisement-label {
	@include font-size(fs-80);

	display: flex;
	padding: 8px;
	color: var(--color-white);
	line-height: 1.2;
	opacity: 0;
	transition-delay: 0ms, 0ms, 0ms;
	transition-duration: 250ms, 250ms, 120ms;
	transition-property: width, height, opacity;
	transition-timing-function: ease;
	pointer-events: none;

	&.is-visible {
		opacity: 1;
		transition-delay: 0ms, 0ms, 250ms;
	}
}

.player:not(.fullscreen) .media-container {
	position: relative;
	aspect-ratio: 16 / 9;
}

.player:not(.fullscreen).mobile.landscape .media-container {
    width: auto;
    height: 100vh;
    margin-left: auto;
    margin-right: auto;
}

.controls {
	position: absolute;
	display: flex;
	flex-direction: column;
	bottom: 0;
	left: 0;
	width: 100%;
	background: var(--color-black);
	padding: 12px 8px;

	button {
		background: var(--color-purple-hill);
		color: var(--color-white);
		border: none;
		padding: 4px 12px;
		border-radius: 4px;
		cursor: pointer;

		&:hover {
			background: var(--color-purple-hill);
		}
	}

	.bottom {
		display: flex;

		.left {
			display: flex;
			flex: 1;

			input {
				margin: 0 8px;
			}

			button + button {
				margin-left: 8px;
			}
		}
	}
}

.loading-spinner {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	-webkit-touch-callout: none;
	user-select: none;
}

.player__ima-container {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	pointer-events: none;
	background-size: cover;
	background-position: center;
	z-index: 2;

	&.is-active {
		pointer-events: auto;
	}
}
</style>
